/** Screens 6, 7, 8 — terminal / one-time states. */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, BadgeCheck, ChevronLeft } from "lucide-react";
import { apiClient } from "../../services/api";

export function Submitted() {
  const navigate = useNavigate();
  return (
    <div
      className="min-h-screen flex flex-col px-6"
      style={{ background: "#F4F3F1" }}
      data-testid="pro-submitted"
    >
      <header className="h-14 flex items-center -ml-2">
        <button
          onClick={() => navigate("/home", { replace: true })}
          aria-label="Back"
          data-testid="submitted-back"
          className="w-10 h-10 grid place-items-center rounded-full hover:bg-white/60 transition-colors"
        >
          <ChevronLeft size={22} />
        </button>
      </header>
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-sm">
          <div
            className="mx-auto rounded-full grid place-items-center"
            style={{ width: 72, height: 72, border: "2px solid #00D4FF" }}
          >
            <Check size={32} color="#00D4FF" strokeWidth={2} />
          </div>
          <h1
            className="font-barlow-cond mt-6"
            style={{ fontSize: 40, fontWeight: 900, color: "#1A1A1A", lineHeight: 1.05 }}
          >
            Application Submitted
          </h1>
          <p
            className="font-barlow mt-3 mx-auto"
            style={{ fontSize: 15, fontWeight: 400, color: "#6B6760", maxWidth: 300, lineHeight: 1.5 }}
          >
            Our team reviews credentials within 24 hours. You will receive a notification once your profile is verified and live.
          </p>
          <button
            onClick={() => navigate("/home", { replace: true })}
            data-testid="submitted-home"
            className="mt-8 px-8"
            style={{
              background: "#00D4FF", color: "#000",
              height: 52, borderRadius: 100,
              fontFamily: "Manrope", fontWeight: 700, fontSize: 15,
              minWidth: 200,
            }}
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

export function VerifiedWelcome({ name, onDismiss }) {
  const navigate = useNavigate();
  const dismiss = async () => {
    try {
      await apiClient.post("/professionals/dismiss-welcome");
    } catch {}
    onDismiss?.();
    navigate("/settings");
  };
  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col items-center justify-center px-6"
      style={{ background: "#F4F3F1" }}
      data-testid="pro-verified-welcome"
    >
      <div className="text-center max-w-sm">
        <div
          className="mx-auto rounded-full grid place-items-center"
          style={{ width: 80, height: 80, background: "#00D4FF" }}
        >
          <BadgeCheck size={40} color="#F4F3F1" strokeWidth={2.4} />
        </div>
        <h1
          className="font-barlow-cond mt-6"
          style={{ fontSize: 44, fontWeight: 900, color: "#1A1A1A", lineHeight: 1 }}
        >
          You are verified
        </h1>
        <h2
          className="font-barlow-cond mt-2"
          style={{ fontSize: 28, fontWeight: 300, color: "#6B6760" }}
        >
          Welcome to FitFrik, {name?.split(" ")[0] || "there"}
        </h2>
        <p
          className="font-barlow mt-3 mx-auto"
          style={{ fontSize: 15, fontWeight: 400, color: "#6B6760", maxWidth: 300, lineHeight: 1.5 }}
        >
          Your profile is now live. Members can find and connect with you on FitFrik.
        </p>
        <button
          onClick={dismiss}
          data-testid="verified-cta"
          className="mt-8 px-8"
          style={{
            background: "#00D4FF", color: "#000",
            height: 52, borderRadius: 100,
            fontFamily: "Manrope", fontWeight: 700, fontSize: 15,
            minWidth: 220,
          }}
        >
          Go to My Profile
        </button>
      </div>
    </div>
  );
}

export function RejectedScreen({ application, onDismiss }) {
  const navigate = useNavigate();
  const reason =
    application?.rejection_reason ||
    "Please ensure your certificate is valid, clearly readable, and matches the details you submitted.";

  const fixAndResubmit = async () => {
    try {
      await apiClient.post("/professionals/dismiss-rejection");
    } catch {}
    onDismiss?.();
    navigate("/pro/apply", { state: { resumeAt: 3 } });
  };

  const support = async () => {
    try {
      await apiClient.post("/professionals/dismiss-rejection");
    } catch {}
    onDismiss?.();
    window.location.href = "mailto:support@fitfrik.in?subject=Professional%20Application%20Help";
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col items-center justify-center px-6"
      style={{ background: "#F4F3F1" }}
      data-testid="pro-rejected"
    >
      <div className="text-center max-w-sm w-full">
        <div
          className="mx-auto rounded-full grid place-items-center"
          style={{ width: 72, height: 72, border: "2px solid #E5484D" }}
        >
          <X size={32} color="#E5484D" strokeWidth={2.2} />
        </div>
        <h1
          className="font-barlow-cond mt-6"
          style={{ fontSize: 40, fontWeight: 900, color: "#1A1A1A", lineHeight: 1.05 }}
        >
          Application Not Approved
        </h1>

        <div
          className="text-left mt-5"
          style={{
            background: "#FFFFFF", border: "1px solid #E2E0DC",
            borderRadius: 12, padding: 16,
          }}
          data-testid="rejection-reason"
        >
          <div
            className="font-barlow uppercase"
            style={{ fontSize: 11, fontWeight: 700, color: "#8E8A84", letterSpacing: "0.08em" }}
          >
            REASON
          </div>
          <p
            className="font-barlow mt-1.5"
            style={{ fontSize: 14, fontWeight: 400, color: "#6B6760", lineHeight: 1.5 }}
          >
            {reason}
          </p>
        </div>

        <p
          className="font-barlow mt-3"
          style={{ fontSize: 14, fontWeight: 400, color: "#6B6760", lineHeight: 1.5 }}
        >
          You can correct your details and resubmit. Your previous information has been saved.
        </p>

        <div className="mt-6 space-y-2">
          <button
            onClick={fixAndResubmit}
            data-testid="rejected-fix"
            className="w-full"
            style={{
              background: "#00D4FF", color: "#000",
              height: 52, borderRadius: 100,
              fontFamily: "Manrope", fontWeight: 700, fontSize: 15,
            }}
          >
            Fix and Resubmit
          </button>
          <button
            onClick={support}
            data-testid="rejected-support"
            className="w-full"
            style={{
              background: "transparent", color: "#1A1A1A",
              border: "1px solid #E2E0DC",
              height: 48, borderRadius: 100,
              fontFamily: "Manrope", fontWeight: 600, fontSize: 14,
            }}
          >
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
}
